import type { ProfileMembership, UserProfile } from './endpoints';
import type { User } from './model';

// Auth0 subs look like `<provider>|<id>` (e.g. `google-oauth2|112...`). When a
// social IdP exposes no name/email claim, the sub itself can leak through as the
// display name — guard against showing it.
export function looksLikeAuth0Sub(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[a-z0-9-]+\|/i.test(value);
}

// Backend sets `email_address` to `<sub>@auth0` when no email claim arrives —
// a valid-looking but undisplayable placeholder.
export function looksLikePlaceholderEmail(value: string | null | undefined): boolean {
  if (!value) return false;
  return /@auth0$/i.test(value);
}

export function displayNameFor(profile?: UserProfile | null, user?: User | null): string {
  const candidates: Array<string | null | undefined> = [
    profile?.full_name,
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' '),
    profile?.email_address,
    user?.name,
    user?.email,
  ];
  for (const c of candidates) {
    if (!c) continue;
    const trimmed = c.trim();
    if (!trimmed) continue;
    if (looksLikeAuth0Sub(trimmed)) continue;
    if (looksLikePlaceholderEmail(trimmed)) continue;
    return trimmed;
  }
  return 'You';
}

export function isOrgAdmin(memberships?: ProfileMembership[]): boolean {
  if (!memberships || memberships.length === 0) return false;
  return memberships.some((m) => m.role === 'admin' || m.role === 'owner');
}

// Accepts both User (auth0 hybrid) and UserProfile (/me): the auth0 copy can lack
// roles until the backend sync completes, so callers may pass either.
export function isSystemAdmin(input?: User | UserProfile | null): boolean {
  if (!input) return false;
  if ('roles' in input && input.roles?.includes('system_admin')) return true;
  if ('role' in input && input.role === 'system_admin') return true;
  return false;
}

// Same shape as isSystemAdmin — system admins also count as instructors so
// staff can QA the instructor surface without flipping their own role.
export function isInstructor(input?: User | UserProfile | null): boolean {
  if (!input) return false;
  if ('roles' in input && (input.roles?.includes('instructor') || input.roles?.includes('system_admin'))) return true;
  if ('role' in input && (input.role === 'instructor' || input.role === 'system_admin')) return true;
  return false;
}

// True only when the user genuinely carries the instructor role OR has an
// instructor record (`has_instructor_profile` from /me) — NOT merely because
// they're a system admin. This is the precise signal for the admin↔instructor
// view switch: a system admin without an instructor record doesn't get it.
export function canActAsInstructor(profile?: UserProfile | null, user?: User | null): boolean {
  if (profile?.has_instructor_profile) return true;
  if (user?.roles?.includes('instructor')) return true;
  if (profile?.role === 'instructor') return true;
  return false;
}
